import React, { useEffect, useRef, useState } from 'react';
import {
  ArrowLeft, Mail, Phone, Pencil, Trash2,
  Upload, Camera, X, KeyRound,
  Briefcase, BadgeCheck, UserX, UserCheck, User, MapPin,
  AlertCircle, Linkedin, FileText, Plus, Download,
} from 'lucide-react';
import {
  Card, CardHeader, CardBody, Button, Badge, Modal, Label,
  Input, ConfirmDeleteModal,
} from '../components/ui';
import { useFirm } from '../contexts/FirmContext';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../contexts/PermissionsContext';
import { useRoute } from '../lib/router';
import {
  fetchEmployee, updateEmployee, deleteEmployee,
  listEmployeeMatters, listFirmRoles,
  updateUserStatus, uploadEmployeePhoto, uploadEmployeeResume,
  getEmployeeResumeUrl, updateEmployeeProfile,
} from '../lib/db';
import { fullName, formatDate } from '../lib/formatters';
import { useActivityLogger } from '../lib/useActivityLogger';
import toast from '../lib/toast';
import ProfileCompletionBar, { computeProfileCompletion } from '../components/onboarding/ProfileCompletionBar';
import CameraCaptureModal from '../components/CameraCaptureModal';

// ─── Constants ────────────────────────────────────────────────────────────────
const ROLE_TONE = {
  Admin: 'red', Partner: 'indigo', Attorney: 'blue', Associate: 'blue',
  Paralegal: 'amber', Clerk: 'slate', IT: 'green', Other: 'slate',
};

const MATTER_STATUS_TONE = {
  open: 'green', in_progress: 'blue', pending: 'amber',
  on_hold: 'slate', completed: 'green', closed: 'slate',
};

// ─── Info row ─────────────────────────────────────────────────────────────────
function InfoRow({ icon: Icon, label, value, href, mono }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-3">
      <div className="w-8 h-8 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center flex-shrink-0 mt-0.5">
        <Icon size={14} className="text-slate-500 dark:text-slate-400" />
      </div>
      <div className="min-w-0">
        <p className="text-xs text-slate-400 dark:text-slate-500">{label}</p>
        {href ? (
          <a href={href} className="text-sm text-indigo-600 dark:text-indigo-400 hover:underline break-all">{value}</a>
        ) : (
          <p className={`text-sm text-slate-800 dark:text-slate-200 break-words ${mono ? 'font-mono font-semibold tracking-wider' : ''}`}>{value}</p>
        )}
      </div>
    </div>
  );
}

// ─── Tag chip input (skills / certs) ─────────────────────────────────────────
function TagInput({ value = [], onChange, placeholder }) {
  const [draft, setDraft] = useState('');
  const add = () => {
    const t = draft.trim();
    if (!t || value.includes(t)) { setDraft(''); return; }
    onChange([...value, t]);
    setDraft('');
  };
  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
          placeholder={placeholder}
          className="flex-1 px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
        />
        <button type="button" onClick={add}
          className="px-3 py-2 rounded-lg bg-indigo-600 text-white text-sm hover:bg-indigo-700 transition">
          <Plus size={14} />
        </button>
      </div>
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {value.map((t) => (
            <span key={t}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 text-xs font-medium">
              {t}
              <button type="button" onClick={() => onChange(value.filter((x) => x !== t))}
                className="hover:text-red-500"><X size={10} /></button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Self-service profile edit modal ─────────────────────────────────────────
function ProfileEditModal({ open, employee, onSaved, onClose }) {
  const photoUploadRef  = useRef(null);
  const resumeRef       = useRef(null);

  const [form, setForm] = useState({});
  const [photoFile, setPhotoFile]   = useState(null);
  const [photoPreview, setPhotoPreview] = useState('');
  const [resumeFile, setResumeFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState('');
  const [errors, setErrors]  = useState({});
  // Camera capture modal — used instead of the old <input capture="user">
  // trick which silently no-op'd on desktop. See CameraCaptureModal.jsx.
  const [showCamera, setShowCamera] = useState(false);

  useEffect(() => {
    if (open && employee) {
      setForm({
        phone:                   employee.phone                   || '',
        date_of_birth:           employee.date_of_birth           || '',
        address:                 employee.address                 || '',
        emergency_contact_name:  employee.emergency_contact_name  || '',
        emergency_contact_phone: employee.emergency_contact_phone || '',
        bio:                     employee.bio                     || '',
        skills:                  employee.skills                  || [],
        linkedin_url:            employee.linkedin_url            || '',
        certifications:          employee.certifications          || [],
      });
      setPhotoFile(null);
      setPhotoPreview(employee.profile_photo_url || '');
      setResumeFile(null);
      setSaveErr('');
      setErrors({});
    }
  }, [open, employee]);

  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handlePhoto = (file) => {
    if (!file) return;
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
  };

  const handleSave = async () => {
    const errs = {};
    if (!form.phone.trim()) errs.phone = 'Phone number is required.';
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setErrors({});
    setSaving(true);
    setSaveErr('');
    try {
      const payload = { ...form };

      if (photoFile) {
        const { url, error: pErr } = await uploadEmployeePhoto(employee.firm_id, employee.id, photoFile);
        if (pErr) throw pErr;
        payload.profile_photo_url = url;
      }
      if (resumeFile) {
        const { path, error: rErr } = await uploadEmployeeResume(employee.firm_id, employee.id, resumeFile);
        if (rErr) throw rErr;
        payload.resume_url = path;
      }

      // If this save would put the profile at 100%, stamp
      // profile_completed_at so the app-wide "needs completion" signals
      // (dashboard banner, sidebar amber dot, name-badge) all flip to
      // "done" together. Otherwise a user who fills everything in via
      // this modal (rather than the original wizard) keeps getting
      // nagged because that flag only gets set by the wizard's save.
      // Conversely, if a previously-complete user clears a required
      // field, we DON'T un-set the flag — once they've finished onboarding
      // they shouldn't be re-onboarded.
      const projected = {
        ...employee,
        ...payload,
        // Match the bar's _resumePending heuristic — a pending upload
        // counts as filled for completion purposes.
        _resumePending: !!resumeFile,
      };
      const pct = computeProfileCompletion(projected).percent;
      if (pct >= 100 && !employee.profile_completed_at) {
        payload.profile_completed_at = new Date().toISOString();
      }

      const { data, error } = await updateEmployeeProfile(employee.id, payload);
      if (error) throw error;
      onSaved(data);
    } catch (err) {
      setSaveErr(err.message || 'Could not save profile.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Edit my profile" size="lg"
      footer={<><Button variant="ghost" onClick={onClose}>Cancel</Button><Button loading={saving} onClick={handleSave}>Save changes</Button></>}>
      <div className="space-y-6">
        {saveErr && (
          <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-xs text-red-700 dark:text-red-400">
            <AlertCircle size={13} className="flex-shrink-0 mt-0.5" /> {saveErr}
          </div>
        )}

        {/* Live completion bar — climbs as fields fill in. We pass a
            merged view of the form draft + a synthetic profile_photo_url
            so picking a photo immediately bumps the bar even before the
            upload completes. */}
        <ProfileCompletionBar
          variant="inline"
          data={{
            ...form,
            profile_photo_url: photoPreview || employee?.profile_photo_url,
            resume_url: employee?.resume_url,
            _resumePending: !!resumeFile,
          }}
        />

        {/* Photo */}
        <div>
          <Label>Profile photo</Label>
          <div className="flex items-center gap-4 mt-1">
            <div className="w-16 h-16 rounded-full bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center flex-shrink-0 overflow-hidden">
              {photoPreview
                ? <img src={photoPreview} alt="" className="w-full h-full object-cover" />
                : <User size={24} className="text-indigo-400" />
              }
            </div>
            <div className="flex gap-2 flex-wrap">
              <button type="button" onClick={() => photoUploadRef.current?.click()}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg border border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-800 transition">
                <Upload size={12} /> Upload
              </button>
              <button type="button" onClick={() => setShowCamera(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg border border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-800 transition">
                <Camera size={12} /> Camera
              </button>
            </div>
            <input ref={photoUploadRef} type="file" accept="image/*" className="hidden"
              onChange={(e) => handlePhoto(e.target.files?.[0])} />
            <CameraCaptureModal
              open={showCamera}
              onClose={() => setShowCamera(false)}
              onCapture={(file) => handlePhoto(file)}
              facingMode="user"
              title="Take a profile photo"
            />
          </div>
        </div>

        {/* Personal */}
        <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-4 space-y-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Personal details</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label>Phone <span className="text-red-500">*</span></Label>
              <Input type="tel" inputMode="tel" autoComplete="tel" value={form.phone}
                onChange={(e) => { setF('phone', e.target.value); setErrors((p) => ({ ...p, phone: undefined })); }}
                className={errors.phone ? 'ring-1 ring-red-400 border-red-400' : ''} />
              {errors.phone && <p className="text-xs text-red-600 mt-1">{errors.phone}</p>}
            </div>
            <div>
              <Label>Date of birth</Label>
              <Input type="date" value={form.date_of_birth}
                onChange={(e) => setF('date_of_birth', e.target.value)} />
            </div>
          </div>
          <div>
            <Label>Home address</Label>
            <textarea rows={2} value={form.address}
              onChange={(e) => setF('address', e.target.value)}
              placeholder="Street, city, region"
              className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label>Emergency contact name</Label>
              <Input value={form.emergency_contact_name}
                onChange={(e) => setF('emergency_contact_name', e.target.value)} />
            </div>
            <div>
              <Label>Emergency contact phone</Label>
              <Input type="tel" value={form.emergency_contact_phone}
                onChange={(e) => setF('emergency_contact_phone', e.target.value)} />
            </div>
          </div>
        </div>

        {/* Professional */}
        <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-4 space-y-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Professional profile</p>
          <div>
            <Label>Bio</Label>
            <textarea rows={3} value={form.bio}
              onChange={(e) => setF('bio', e.target.value)}
              placeholder="A short summary of your background and expertise…"
              className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none" />
          </div>
          <div>
            <Label>Skills</Label>
            <TagInput value={form.skills || []}
              onChange={(v) => setF('skills', v)}
              placeholder="e.g. Contract law — press Enter" />
          </div>
          <div>
            <Label>LinkedIn URL</Label>
            <Input type="url" value={form.linkedin_url}
              onChange={(e) => setF('linkedin_url', e.target.value)}
              placeholder="https://linkedin.com/in/…" />
          </div>
          <div>
            <Label>Certifications / qualifications</Label>
            <TagInput value={form.certifications || []}
              onChange={(v) => setF('certifications', v)}
              placeholder="e.g. LLB, MBA — press Enter" />
          </div>
        </div>

        {/* Resume */}
        <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-4 space-y-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Resume / CV</p>
          {resumeFile ? (
            <div className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
              <FileText size={18} className="text-indigo-500" />
              <p className="text-sm font-medium flex-1 truncate">{resumeFile.name}</p>
              <button type="button" onClick={() => setResumeFile(null)} className="text-slate-400 hover:text-red-500">
                <X size={14} />
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              {employee?.resume_url && (
                <p className="text-xs text-green-600 dark:text-green-400 flex items-center gap-1">
                  <BadgeCheck size={12} /> Resume on file
                </p>
              )}
              <button type="button" onClick={() => resumeRef.current?.click()}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg border border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-800 transition">
                <Upload size={12} /> {employee?.resume_url ? 'Replace resume' : 'Upload resume'}
              </button>
            </div>
          )}
          <input ref={resumeRef} type="file"
            accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            className="hidden"
            onChange={(e) => setResumeFile(e.target.files?.[0] || null)} />
        </div>
      </div>
    </Modal>
  );
}

// ─── Edit employee modal ──────────────────────────────────────────────────────
function EditEmployeeModal({ open, employee, firmId, firmRoles, onSaved, onClose }) {
  // Ghana card capture was removed from the employee profile — it
  // duplicated the contact-level Ghana card flow and added regulated
  // PII (national ID + photo of card) inside the firm without a clear
  // legitimate-purpose policy. If we bring it back later, the
  // `ghana_card_number` / `ghana_card_url` columns on `employees` are
  // still there to receive data — only the UI was stripped.
  const blank = {
    first_name: '', last_name: '', email: '', phone: '',
    title: '', hourly_rate: '', notes: '',
  };
  const [form, setForm]           = useState(blank);
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving]       = useState(false);

  useEffect(() => {
    if (open && employee) {
      setForm({
        first_name:  employee.first_name  || '',
        last_name:   employee.last_name   || '',
        email:       employee.email       || '',
        phone:       employee.phone       || '',
        title:       employee.title       || '',
        hourly_rate: employee.hourly_rate || '',
        notes:       employee.notes       || '',
      });
      setFormErrors({});
    }
  }, [open, employee]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSave = async () => {
    const errs = {};
    if (!form.phone.trim()) errs.phone = 'Phone number is required.';
    if (Object.keys(errs).length > 0) { setFormErrors(errs); return; }
    setFormErrors({});
    setSaving(true);
    try {
      const payload = { ...form, hourly_rate: form.hourly_rate ? Number(form.hourly_rate) : 0 };
      const { data, error } = await updateEmployee(employee.id, payload);
      if (error) { toast.error(error.message); return; }
      onSaved(data);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Edit employee" size="lg"
      footer={<><Button variant="ghost" onClick={onClose}>Cancel</Button><Button loading={saving} onClick={handleSave}>Save changes</Button></>}>
      <div className="space-y-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div><Label required>First name</Label><Input value={form.first_name} onChange={(e) => setField('first_name', e.target.value)} /></div>
          <div><Label>Last name</Label><Input value={form.last_name} onChange={(e) => setField('last_name', e.target.value)} /></div>
          <div><Label>Job title</Label><Input value={form.title} onChange={(e) => setField('title', e.target.value)} placeholder="e.g. Senior Associate" /></div>
          <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setField('email', e.target.value)} /></div>
          <div>
            <Label>Phone <span className="text-red-500">*</span></Label>
            <Input type="tel" inputMode="tel" autoComplete="tel" value={form.phone} onChange={(e) => { setField('phone', e.target.value); setFormErrors((p) => ({ ...p, phone: undefined })); }} className={formErrors.phone ? 'ring-red-400' : ''} />
            {formErrors.phone && <p className="text-xs text-red-600 mt-1">{formErrors.phone}</p>}
          </div>
          <div><Label>Hourly rate (GHS)</Label><Input type="number" step="0.01" value={form.hourly_rate} onChange={(e) => setField('hourly_rate', e.target.value)} /></div>
          <div className="sm:col-span-2"><Label>Notes</Label><Input value={form.notes} onChange={(e) => setField('notes', e.target.value)} /></div>
        </div>

      </div>
    </Modal>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────
export default function EmployeeDetailPage({ employeeId }) {
  const [, navigate]              = useRoute();
  const { firmId }                = useFirm();
  const { user }                  = useAuth();
  const { can }                   = usePermissions();
  const log                       = useActivityLogger();

  const [employee,  setEmployee]  = useState(null);
  const [matters,   setMatters]   = useState([]);
  const [firmRoles, setFirmRoles] = useState([]);
  const [loading,   setLoading]   = useState(true);

  const [showEdit,        setShowEdit]        = useState(false);
  const [showProfileEdit, setShowProfileEdit] = useState(false);
  const [showDelete,      setShowDelete]      = useState(false);
  const [deleting,        setDeleting]        = useState(false);
  const [resumeLoading,   setResumeLoading]   = useState(false);

  useEffect(() => {
    if (!employeeId) return;
    (async () => {
      setLoading(true);
      try {
        const [{ data: e }, { data: mt }, { data: roles }] = await Promise.all([
          fetchEmployee(employeeId),
          listEmployeeMatters(employeeId),
          firmId ? listFirmRoles(firmId) : Promise.resolve({ data: [] }),
        ]);
        setEmployee(e || null);
        setMatters((mt || []).map((r) => ({ ...r.matter, role_on_matter: r.role_on_matter })));
        setFirmRoles(roles || []);

        // Auto-open the self-service profile modal when the reminder
        // banner explicitly sent the user here. Removes the confusing
        // two-click handoff where "Complete profile" lands them on a
        // read-only view. We do NOT auto-open on every visit to one's
        // own profile — that would feel pushy once a user just wants to
        // glance at their record without editing.
        let bannerFlag = false;
        try {
          bannerFlag = !!sessionStorage.getItem(`lf_open_profile_edit_${employeeId}`);
          if (bannerFlag) sessionStorage.removeItem(`lf_open_profile_edit_${employeeId}`);
        } catch { /* ignore */ }
        if (e && bannerFlag) setShowProfileEdit(true);
      } catch (err) {
        console.error('EmployeeDetailPage fetch error:', err);
      } finally {
        setLoading(false);
      }
    })();
  }, [employeeId, firmId]);

  const handleSaved = (updated) => {
    setEmployee(updated);
    setShowEdit(false);
    log('employee', updated.id, 'updated', `Employee "${fullName(updated)}" updated`);
  };

  const handleProfileSaved = (updated) => {
    setEmployee(updated);
    setShowProfileEdit(false);
  };

  const handleResumeDownload = async () => {
    if (!employee?.resume_url) return;
    setResumeLoading(true);
    const { url, error } = await getEmployeeResumeUrl(employee.resume_url);
    setResumeLoading(false);
    if (error || !url) { toast.error('Could not generate download link. Please try again.'); return; }
    window.open(url, '_blank');
  };

  const handleToggleStatus = async () => {
    const next = employee.status === 'active' ? 'inactive' : 'active';
    setEmployee((e) => ({ ...e, status: next }));
    await updateEmployee(employee.id, { status: next });
    if (employee.user_id) await updateUserStatus(employee.user_id, next);
  };

  const handleDelete = async () => {
    setDeleting(true);
    await deleteEmployee(employee.id);
    log('employee', employee.id, 'deleted', 'Employee removed');
    navigate('/employees');
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><p className="text-sm text-slate-400">Loading…</p></div>;
  }
  if (!employee) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3">
        <p className="text-sm text-slate-500">Employee not found.</p>
        <Button variant="ghost" icon={ArrowLeft} onClick={() => navigate('/employees')}>Back to employees</Button>
      </div>
    );
  }

  // Access control:
  // canManage → admin / IT / any user with team.manage permission → full access
  // isOwnProfile → the logged-in user is viewing their own record → can view + edit (but not deactivate/delete)
  // anyone else → blocked
  const canManage    = can('team.manage');
  const isOwnProfile = !!user && !!employee.user_id && employee.user_id === user.id;
  // Anyone authenticated can VIEW — only own-profile or admin can EDIT
  // eslint-disable-next-line no-unused-vars
  const _canEdit = canManage || isOwnProfile;

  const openMatters = matters.filter((m) => ['open', 'in_progress', 'pending'].includes(m.status));
  // The completion card has its own "Complete profile" CTA. When it's
  // visible, the action-bar "Edit Profile" button is redundant — both
  // open the same modal. Hide the header button in that case so there's
  // exactly one edit affordance. At 100% the card disappears and the
  // header button takes over.
  const completionPercent = isOwnProfile ? computeProfileCompletion(employee).percent : 100;
  const showHeaderEditProfile = isOwnProfile && completionPercent >= 100;

  return (
    <div className="space-y-6">
      {/* Back + actions */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <button onClick={() => navigate('/employees')}
          className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 dark:hover:text-slate-200 transition">
          <ArrowLeft size={16} /> Employees
        </button>
        <div className="flex items-center gap-2">
          {canManage && (
            <Button
              variant="secondary"
              icon={employee.status === 'active' ? UserX : UserCheck}
              size="sm"
              onClick={handleToggleStatus}
            >
              {employee.status === 'active' ? 'Deactivate' : 'Activate'}
            </Button>
          )}
          {showHeaderEditProfile && (
            <Button variant="secondary" icon={User} size="sm" onClick={() => setShowProfileEdit(true)}>Edit Profile</Button>
          )}
          {canManage && (
            <Button variant="secondary" icon={Pencil} size="sm" onClick={() => setShowEdit(true)}>Edit</Button>
          )}
          {canManage && (
            <Button variant="danger" icon={Trash2} size="sm" onClick={() => setShowDelete(true)}>Remove</Button>
          )}
        </div>
      </div>

      {/* Profile header */}
      <div className="flex items-start gap-5 flex-wrap">
        {/* Avatar — show profile photo if available, else initials */}
        <div className="w-16 h-16 rounded-2xl bg-indigo-100 dark:bg-indigo-900/40 flex items-center justify-center flex-shrink-0 overflow-hidden">
          {employee.profile_photo_url
            ? <img src={employee.profile_photo_url} alt={fullName(employee)} className="w-full h-full object-cover" />
            : <span className="text-indigo-700 dark:text-indigo-300 font-bold text-xl">
                {(employee.first_name?.[0] || '?')}{employee.last_name?.[0] || ''}
              </span>
          }
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-white">
              {fullName(employee) || '—'}
            </h1>
            {employee.role && <Badge tone={ROLE_TONE[employee.role] || 'slate'}>{employee.role}</Badge>}
            <Badge tone={employee.status === 'active' ? 'green' : 'slate'}>{employee.status}</Badge>
            {employee.user_id && (
              <span className="inline-flex items-center gap-1 text-xs text-indigo-600 dark:text-indigo-400 font-medium bg-indigo-50 dark:bg-indigo-900/20 px-2 py-0.5 rounded-full">
                <KeyRound size={11} /> App access
              </span>
            )}
            {employee.profile_completed_at && (
              <span className="inline-flex items-center gap-1 text-xs text-green-600 dark:text-green-400 font-medium bg-green-50 dark:bg-green-900/20 px-2 py-0.5 rounded-full">
                <BadgeCheck size={11} /> Profile complete
              </span>
            )}
          </div>
          {employee.title && <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">{employee.title}</p>}
          {employee.employee_id && (
            <p className="text-xs font-mono text-indigo-600 dark:text-indigo-400 mt-1">{employee.employee_id}</p>
          )}
          {/* Bio shown inline under name if present */}
          {employee.bio && (
            <p className="text-sm text-slate-600 dark:text-slate-400 mt-1 max-w-xl">{employee.bio}</p>
          )}
        </div>
      </div>

      {/* Profile completion — only on own profile. Stays visible until
          every section is filled in (not just until the user saves once
          via the wizard), so it nudges them to add the optional touches
          like bio + skills even after the first save. Hides at 100% so
          the page doesn't shout at a fully set-up user; the action-bar
          "Edit Profile" button takes over at that point (see above). */}
      {isOwnProfile && completionPercent < 100 && (
        <ProfileCompletionBar
          variant="card"
          data={employee}
          onAction={() => setShowProfileEdit(true)}
        />
      )}

      {/* KPI strip */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {[
          { label: 'Assigned matters', value: matters.length },
          { label: 'Active matters',   value: openMatters.length },
          { label: 'Hourly rate',      value: employee.hourly_rate > 0 ? `GHS ${Number(employee.hourly_rate).toFixed(2)}` : '—' },
        ].map(({ label, value }) => (
          <Card key={label}>
            <CardBody className="py-4">
              <p className="text-xl font-bold text-slate-800 dark:text-slate-100">{value}</p>
              <p className="text-xs text-slate-500 mt-0.5">{label}</p>
            </CardBody>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column */}
        <div className="space-y-4">

          {/* Contact & employment details */}
          <Card>
            <CardHeader title="Details" />
            <CardBody className="space-y-4">
              <InfoRow icon={Mail}    label="Email"        value={employee.email} href={`mailto:${employee.email}`} />
              <InfoRow icon={Phone}   label="Phone"        value={employee.phone} href={`tel:${employee.phone}`} />
              <InfoRow icon={Briefcase} label="Hourly rate" value={employee.hourly_rate > 0 ? `GHS ${Number(employee.hourly_rate).toFixed(2)}/hr` : null} />
              {employee.date_of_birth && (
                <InfoRow icon={User} label="Date of birth" value={formatDate(employee.date_of_birth)} />
              )}
              {employee.address && (
                <InfoRow icon={MapPin} label="Home address" value={employee.address} />
              )}
              {employee.notes && (
                <div className="pt-2 border-t border-slate-100 dark:border-slate-800">
                  <p className="text-xs text-slate-400 mb-1">Notes</p>
                  <p className="text-sm text-slate-700 dark:text-slate-300 whitespace-pre-wrap">{employee.notes}</p>
                </div>
              )}
            </CardBody>
          </Card>

          {/* Emergency contact */}
          {(employee.emergency_contact_name || employee.emergency_contact_phone) && (
            <Card>
              <CardHeader title="Emergency contact" />
              <CardBody className="space-y-3">
                <InfoRow icon={User}  label="Name"  value={employee.emergency_contact_name} />
                <InfoRow icon={Phone} label="Phone" value={employee.emergency_contact_phone} href={employee.emergency_contact_phone ? `tel:${employee.emergency_contact_phone}` : undefined} />
              </CardBody>
            </Card>
          )}

          {/* Professional profile */}
          {(employee.skills?.length || employee.linkedin_url || employee.certifications?.length) && (
            <Card>
              <CardHeader title="Professional" />
              <CardBody className="space-y-4">
                {employee.linkedin_url && (
                  <InfoRow icon={Linkedin} label="LinkedIn" value={employee.linkedin_url}
                    href={employee.linkedin_url} />
                )}
                {employee.skills?.length > 0 && (
                  <div>
                    <p className="text-xs text-slate-400 mb-2">Skills</p>
                    <div className="flex flex-wrap gap-1.5">
                      {employee.skills.map((s) => (
                        <span key={s} className="inline-block px-2.5 py-1 rounded-full bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 text-xs font-medium">
                          {s}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {employee.certifications?.length > 0 && (
                  <div>
                    <p className="text-xs text-slate-400 mb-2">Certifications</p>
                    <div className="flex flex-wrap gap-1.5">
                      {employee.certifications.map((c) => (
                        <span key={c} className="inline-block px-2.5 py-1 rounded-full bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 text-xs font-medium">
                          {c}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </CardBody>
            </Card>
          )}

          {/* Resume */}
          {employee.resume_url && (
            <Card>
              <CardHeader title="Resume / CV" />
              <CardBody>
                <button
                  onClick={handleResumeDownload}
                  disabled={resumeLoading}
                  className="flex items-center gap-2 text-sm text-indigo-600 dark:text-indigo-400 hover:underline disabled:opacity-50"
                >
                  {resumeLoading
                    ? <><span className="animate-spin">⏳</span> Generating link…</>
                    : <><Download size={14} /> Download resume</>
                  }
                </button>
              </CardBody>
            </Card>
          )}

        </div>

        {/* Right column — matters */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader title={`Assigned matters (${matters.length})`} />
            {matters.length === 0 ? (
              <CardBody>
                <p className="text-sm text-slate-400 text-center py-6">Not assigned to any matters yet.</p>
              </CardBody>
            ) : (
              <div className="divide-y divide-slate-100 dark:divide-slate-800">
                {matters.map((m) => (
                  <div key={m.id}
                    className="flex items-center justify-between gap-4 px-6 py-3 hover:bg-slate-50 dark:hover:bg-slate-800/40 cursor-pointer transition"
                    onClick={() => navigate(`/matters/${m.id}`)}>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-800 dark:text-slate-200 truncate">{m.title}</p>
                      <p className="text-xs text-slate-400 mt-0.5">
                        {m.matter_number}
                        {m.practice_area ? ` · ${m.practice_area}` : ''}
                        {m.role_on_matter ? ` · ${m.role_on_matter.replace('_', ' ')}` : ''}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Badge tone={MATTER_STATUS_TONE[m.status] || 'slate'}>{m.status?.replace('_', ' ')}</Badge>
                      <p className="text-xs text-slate-400">{formatDate(m.created_at)}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      </div>

      {/* Self-service profile edit modal (own profile) */}
      <ProfileEditModal
        open={showProfileEdit}
        employee={employee}
        onSaved={handleProfileSaved}
        onClose={() => setShowProfileEdit(false)}
      />

      {/* Admin edit modal (employment fields) */}
      <EditEmployeeModal
        open={showEdit}
        employee={employee}
        firmId={firmId}
        firmRoles={firmRoles}
        onSaved={handleSaved}
        onClose={() => setShowEdit(false)}
      />

      {/* Delete confirmation */}
      <ConfirmDeleteModal
        open={showDelete}
        onClose={() => setShowDelete(false)}
        loading={deleting}
        title="Remove employee"
        description={<>Remove <strong>{fullName(employee)}</strong> from the firm? This cannot be undone.</>}
        onConfirm={handleDelete}
      />
    </div>
  );
}
